// @ts-strict-ignore
import { FC, useEffect, useMemo } from 'react';

import { Box, css, styled } from '@mui/material';
import { AnalyticEventAction } from 'analytics';

import { trackShowMoreAnalyticsEvent } from 'analytics/events/show-more';

import { observer } from 'mobx-react';

import useNetworkLoading from 'mobx/hooks/useNetworkLoading';

import { useStores } from 'mobx/hooks/useStores';

import { getDateMonthString } from 'utils/DateUtils';

import { API_LABELS } from 'constants/apiUrls';

import ClusteredTaskTicket from 'models/ClusteredTaskTicket';

import { Language, languageOptionsMap } from 'models/Patient';
import Ticket from 'models/Ticket';

import { LazyCardStack } from 'views/Widgets/CardStack';

import { TicketSectionId } from 'views/WorkQueue/WorkQueue.types';

import Chip from 'components/Chips/Chip';
import { usePatientModel } from 'components/Patient/usePatientModel';

import { CardRow } from 'components/Surfaces/Card';
import EpisodeTag from 'components/Ticket/TicketRow/EpisodeTag';
import TaskTicketRow, { TaskRowDueDateIcon } from 'components/Ticket/TicketRow/TaskTicketRow';
import TicketRenderer from 'components/Ticket/TicketRow/TicketRenderer';
import { TicketNewMessagesExpander } from 'components/Ticket/TicketRow/TicketSummaryButton/TicketNewMessagesExpander';
import { TicketStyledSummaryItem } from 'components/Ticket/TicketsContainers/TicketStyledSummaryItem';
import { TextButton } from 'components/UIkit/atoms/Button';
import { Text } from 'components/UIkit/atoms/Text';

interface TaskTicketItemProps {
  clusteredTask: ClusteredTaskTicket;
  ticketIndex: number;
  ticketSectionCurrentPage: number;
  ticketSectionId: TicketSectionId;
}

export const ClusteredTasksItem = observer(
  ({
    clusteredTask,
    ticketIndex,
    ticketSectionCurrentPage,
    ticketSectionId
  }: TaskTicketItemProps) => {
    if (clusteredTask.singleTask) {
      return (
        <CardRow>
          <TaskTicketRow
            ticket={clusteredTask.singleTask}
            ticketIndex={ticketIndex}
            withPatientLink
            ticketSectionCurrentPage={ticketSectionCurrentPage}
            ticketSectionId={ticketSectionId}
          />
        </CardRow>
      );
    } else {
      return (
        <ClusteredTasksRow
          clusteredTask={clusteredTask}
          ticketSectionCurrentPage={ticketSectionCurrentPage}
          ticketSectionId={ticketSectionId}
        />
      );
    }
  }
);

const ClusteredTasksRow: FC<{
  clusteredTask: ClusteredTaskTicket;
  ticketSectionCurrentPage: number;
  ticketSectionId: TicketSectionId;
}> = observer(({ clusteredTask, ticketSectionCurrentPage, ticketSectionId }) => {
  const { tasksStore } = useStores();
  const isOpen = tasksStore.patientIdsToFetch.has(clusteredTask.patientId);
  const items = tasksStore.tasksForPatientCluster(clusteredTask.patientId);

  const isLoadingTasks = useNetworkLoading(API_LABELS.TASKS_BY_PATIENT(clusteredTask.patientId));

  useEffect(
    function onExpandChangedEffect() {
      isOpen
        ? tasksStore.setTasksForPatientCluster(clusteredTask.patientId)
        : tasksStore.clearTasksForPatientCluster(clusteredTask.patientId);
    },
    [clusteredTask.patientId, isOpen, tasksStore]
  );

  const onToggle = () => {
    const isOpen = tasksStore.patientIdsToFetch.has(clusteredTask.patientId);
    trackShowMoreAnalyticsEvent({
      action: isOpen ? AnalyticEventAction.Hide : AnalyticEventAction.Show,
      value: 'task',
      page_number: ticketSectionCurrentPage + 1,
      type: null,
      with_message: false
    });
    isOpen
      ? tasksStore.patientIdsToFetch.delete(clusteredTask.patientId)
      : tasksStore.patientIdsToFetch.add(clusteredTask.patientId);
  };

  const itemsToRender = useMemo(
    () =>
      items?.map((taskTicket: Ticket, index) => (
        <TaskTicketRow
          key={taskTicket.id}
          ticket={taskTicket}
          className="no-border"
          withPatientLink
          ticketIndex={index}
          ticketSectionCurrentPage={ticketSectionCurrentPage}
          ticketSectionId={ticketSectionId}
        />
      )),
    [items, ticketSectionCurrentPage, ticketSectionId]
  );

  return (
    <LazyCardStack
      isOpen={isOpen}
      classes={{
        root: 'ticket-row',
        closed: 'report-row-closed'
      }}
      trigger={
        <ClusteredSummaryRow
          isExpanded={isOpen}
          onToggle={onToggle}
          isLoading={isLoadingTasks}
          clusteredTask={clusteredTask}
        />
      }
      items={itemsToRender}
    />
  );
});

const ClusteredSummaryRow = ({
  clusteredTask,
  onToggle,
  isExpanded,
  isLoading
}: {
  clusteredTask: ClusteredTaskTicket;
  onToggle: () => void;
  isExpanded: boolean;
  isLoading?: boolean;
}) => {
  const { constantsStore } = useStores();
  const { monthNumbers: months, episodeIds, taskCount } = clusteredTask;

  const patient = usePatientModel(clusteredTask.patientId);
  const toggleButtonText = `${isExpanded ? 'hide' : 'show'} ${taskCount} tasks`;

  const episodesChips = useMemo(
    () =>
      [...episodeIds].sort().map((episodeId) => {
        const episode = episodeId !== -1 ? constantsStore.getEpisodeById(episodeId) : null;
        return <EpisodeTag key={episodeId} episode={episode} />;
      }),
    [constantsStore, episodeIds]
  );

  const monthsChip = useMemo(() => {
    if (!months.length) {
      return null;
    }

    return (
      <Chip.Item className="mb-2">Months: {[...months].sort((a, b) => a - b).join(', ')}</Chip.Item>
    );
  }, [months]);
  const languageText =
    patient && patient.language !== Language.English
      ? ` — Prefers ${languageOptionsMap[patient.language]}`
      : '';
  return (
    <TicketRenderer
      patient={patient}
      isLoading={isLoading}
      withPatientLink
      icon={
        <TaskRowDueDateIcon header={getDateMonthString(clusteredTask.dueDateMin).toUpperCase()}>
          {clusteredTask.dueDateByTypeAsNumber('dueDateMin')}
        </TaskRowDueDateIcon>
      }
      classes={{ container: 'ticket-row no-border' }}
      summary={
        <>
          <div className="title-text">
            {taskCount} Tasks Due from {clusteredTask.formattedDateByType('dueDateMin')} to{' '}
            {clusteredTask.formattedDateByType('dueDateMax')}
            {languageText && (
              <Text variant="form-text" color="secondary">
                {languageText}
              </Text>
            )}
          </div>
          <Chip.List classes="mt-1">
            {episodesChips}
            {monthsChip}
          </Chip.List>

          <StyledContainer>
            {clusteredTask.newMessageCount > 0 && (
              <TicketStyledSummaryItem onClick={onToggle}>
                <TicketNewMessagesExpander count={clusteredTask.newMessageCount} />
              </TicketStyledSummaryItem>
            )}
            <TicketStyledSummaryItem>
              <TextButton onClick={onToggle} variant="secondary">
                {toggleButtonText}
              </TextButton>
            </TicketStyledSummaryItem>
          </StyledContainer>
        </>
      }
    />
  );
};

const StyledContainer = styled(Box)(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    margin-top: ${theme.spacing(4)};
    line-height: 16px;
    height: 24px;
  `
);
